const AUTH_URL = import.meta.env.VITE_AUTH_SERVER || 'http://localhost:8787';

/**
 * Report this visit's Core Web Vitals to our own endpoint.
 *
 * Cloudflare Web Analytics already collects these, but at one visitor a day
 * its panel returns a cached aggregate - the same LCP figure to the
 * millisecond across a 7-day, a 6-hour and a 30-minute window - so it cannot
 * say what any individual visit measured. That makes it useless for the only
 * question a page-speed change raises: did this actually help?
 *
 * These numbers land in event_log, one row per metric, queryable directly.
 * `prerendered` is the field that matters: it records whether the visitor got
 * the build-time HTML or fell through to the client-rendered path.
 *
 * Everything here is best-effort. No metric is worth breaking a page for, so
 * every branch is guarded and every failure is silent.
 */
interface Vital {
  metric: 'LCP' | 'CLS';
  value: number;
  element?: string;
  path: string;
  viewport: number;
  prerendered: boolean;
}

function send(vital: Vital) {
  const url = `${AUTH_URL}/auth/metric/webvital`;
  const payload = JSON.stringify(vital);
  try {
    // Beacons survive the page going away, which is exactly when this fires.
    if (navigator.sendBeacon?.(url, new Blob([payload], { type: 'application/json' }))) return;
  } catch {
    /* fall through to fetch */
  }
  try {
    void fetch(url, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: payload, keepalive: true });
  } catch {
    /* a missing metric is not worth a thrown error */
  }
}

/** Short, stable description of the element that painted, for diagnosis. */
function describe(el: Element | null): string | undefined {
  if (!el) return undefined;
  const cls = String((el as HTMLElement).className || '')
    .split(' ')
    .filter(Boolean)
    .slice(0, 2)
    .join('.');
  return cls ? `${el.tagName.toLowerCase()}.${cls}` : el.tagName.toLowerCase();
}

export function reportWebVitals() {
  if (typeof PerformanceObserver === 'undefined') return;

  const prerendered = document.getElementById('root')?.dataset.prerendered === '1';
  let lcp: { value: number; element?: string } | null = null;
  let cls = 0;
  let sent = false;

  try {
    new PerformanceObserver((list) => {
      const entries = list.getEntries() as Array<PerformanceEntry & { element?: Element; size?: number }>;
      // The last candidate wins: LCP keeps updating until the page is
      // interacted with or hidden, which is the point - a late repaint is
      // exactly the failure being measured.
      const last = entries[entries.length - 1];
      if (last) lcp = { value: last.startTime, element: describe(last.element ?? null) };
    }).observe({ type: 'largest-contentful-paint', buffered: true });

    new PerformanceObserver((list) => {
      for (const entry of list.getEntries() as Array<PerformanceEntry & { value?: number; hadRecentInput?: boolean }>) {
        // Shifts within 500ms of a user input are that input's doing, not the
        // page misbehaving, and are excluded from CLS by definition.
        if (!entry.hadRecentInput && typeof entry.value === 'number') cls += entry.value;
      }
    }).observe({ type: 'layout-shift', buffered: true });
  } catch {
    // An engine without these entry types reports nothing rather than throwing.
    return;
  }

  const flush = () => {
    if (sent) return;
    sent = true;
    const base = {
      path: location.pathname.slice(0, 120),
      viewport: window.innerWidth,
      prerendered,
    };
    if (lcp) send({ metric: 'LCP', value: lcp.value, element: lcp.element, ...base });
    if (cls > 0) send({ metric: 'CLS', value: cls, ...base });
  };

  // Sent when the page is hidden, not on load: LCP is not final until then,
  // and pagehide covers the cases visibilitychange misses on iOS.
  //
  // visibilitychange is fired at the document. It happens to reach window
  // listeners in Chrome because it bubbles, which is exactly the kind of
  // detail that works everywhere you test and fails on someone's phone.
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'hidden') flush();
  });
  window.addEventListener('pagehide', flush);
}
